"use client";

/**
 * A `fill` SafeImage whose `src` changes crossfade instead of hard-cutting.
 *
 * Two stacked layers that are never remounted: the base (always opaque) and a
 * top layer whose opacity is toggled. A new `src` loads into whichever layer
 * is hidden; only once it has loaded does the top layer fade in (new photo on
 * top) or out (new photo underneath). The current photo stays on screen the
 * whole time, so there is no blank frame while the next one downloads.
 *
 * Used for variant-swatch clicks on product cards. Reduced-motion users get
 * an instant swap.
 */

import { type ComponentProps, useEffect, useState } from "react";
import { SafeImage } from "@/components/storefront/SafeImage";

type SafeImageProps = ComponentProps<typeof SafeImage>;

type CrossfadeImageProps = Omit<SafeImageProps, "src" | "onLoad" | "onError"> & {
  src: string | null | undefined;
  /** The image `blurDataURL` belongs to; other photos get no blur. */
  blurSrc?: string | null;
};

type Layers = {
  /** [base, top]. Top is null until the first swap. */
  srcs: [string | null | undefined, string | null | undefined];
  /** Which layers have finished loading (or failed) their current src. */
  ready: [boolean, boolean];
  topVisible: boolean;
};

export function CrossfadeImage({
  src,
  blurSrc,
  blurDataURL,
  priority,
  fetchPriority,
  ...rest
}: CrossfadeImageProps) {
  const [layers, setLayers] = useState<Layers>({
    srcs: [src, null],
    ready: [true, false],
    topVisible: false,
  });

  useEffect(() => {
    setLayers((l) => {
      const shown = l.topVisible ? 1 : 0;
      const hidden = l.topVisible ? 0 : 1;
      // Back to the photo on screen: nothing to do (a half-loaded photo in
      // the hidden layer can no longer come forward — settle() checks).
      if (l.srcs[shown] === src) return l;
      if (l.srcs[hidden] === src && l.ready[hidden]) {
        // Already loaded underneath/on top: just flip.
        return { ...l, topVisible: hidden === 1 };
      }
      const srcs = [...l.srcs] as Layers["srcs"];
      const ready = [...l.ready] as Layers["ready"];
      srcs[hidden] = src;
      ready[hidden] = false;
      return { ...l, srcs, ready };
    });
  }, [src]);

  function settle(layer: 0 | 1, loadedSrc: string | null | undefined) {
    setLayers((l) => {
      // A later click may already have replaced this layer's src.
      if (l.srcs[layer] !== loadedSrc) return l;
      const ready = [...l.ready] as Layers["ready"];
      ready[layer] = true;
      // Only the latest requested photo may come to the front.
      const wanted = l.srcs[layer] === src;
      return {
        ...l,
        ready,
        topVisible: wanted ? layer === 1 : l.topVisible,
      };
    });
  }

  const renderLayer = (layer: 0 | 1) => {
    const layerSrc = layers.srcs[layer];
    const isTop = layer === 1;
    return (
      <div
        key={layer}
        data-crossfade-layer={isTop ? "top" : "base"}
        className={
          isTop
            ? `absolute inset-0 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
                layers.topVisible ? "opacity-100" : "opacity-0"
              }`
            : "absolute inset-0"
        }
      >
        <SafeImage
          {...rest}
          src={layerSrc}
          blurDataURL={layerSrc && layerSrc === blurSrc ? blurDataURL : null}
          // Only the first photo is the page's LCP candidate.
          priority={isTop ? undefined : priority}
          fetchPriority={isTop ? undefined : fetchPriority}
          onLoad={() => settle(layer, layerSrc)}
          onError={() => settle(layer, layerSrc)}
        />
      </div>
    );
  };

  return (
    <>
      {renderLayer(0)}
      {layers.srcs[1] !== null && renderLayer(1)}
    </>
  );
}
